import React from 'react';
import { 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Users, 
  Calendar,
  Shield,
  CheckCircle,
  UserCheck,
  Contact,
  Home,
  Badge
} from 'lucide-react';

const ParentInfoTab = ({ parent }) => {
  const InfoItem = ({ icon: Icon, label, value, highlight = false }) => (
    <div className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all duration-200">
      <div className={`p-2 rounded-lg mr-3 ${highlight ? 'bg-blue-100' : 'bg-gray-100'}`}>
        <Icon className={`h-5 w-5 ${highlight ? 'text-blue-600' : 'text-gray-600'}`} />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-600">{label}</p>
        <p className={`text-lg font-semibold ${highlight ? 'text-blue-600' : 'text-gray-900'}`}>
          {value || 'Non renseigné'}
        </p>
      </div>
    </div>
  );

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Non renseigné';
    const phoneStr = phone.toString();
    return phoneStr.replace(/(\d{2})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec avatar */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-6">
        <div className="flex items-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mr-6">
            <User className="h-10 w-10 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {parent?.prenom_parent} {parent?.nom_parent}
            </h2>
            <div className="flex items-center mt-2">
              <div className="flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm mr-3">
                <CheckCircle className="h-4 w-4 mr-1" />
                Profil actif
              </div>
              <div className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                <Users className="h-4 w-4 mr-1" />
                {parent?.nombre_etudiant} étudiant{parent?.nombre_etudiant > 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Informations personnelles */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg mr-3">
              <Contact className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Informations Personnelles</h3>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoItem 
              icon={User} 
              label="Nom" 
              value={parent?.nom_parent}
              highlight={true}
            />
            <InfoItem 
              icon={UserCheck} 
              label="Prénom" 
              value={parent?.prenom_parent}
              highlight={true}
            />
            <InfoItem 
              icon={Phone} 
              label="Numéro de téléphone" 
              value={formatPhoneNumber(parent?.contact_parent)}
            />
            <InfoItem 
              icon={Mail} 
              label="Adresse email" 
              value={parent?.email_parent}
            />
            <InfoItem 
              icon={MapPin} 
              label="Adresse" 
              value={parent?.adresse_parent}
            />
            <InfoItem 
              icon={Badge} 
              label="Identifiant parent" 
              value={`#${parent?.id}`}
            />
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg mr-3">
              <Users className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Statistiques</h3>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{parent?.nombre_etudiant || 0}</p>
              <p className="text-sm text-gray-600">Étudiant{parent?.nombre_etudiant > 1 ? 's' : ''} inscrit{parent?.nombre_etudiant > 1 ? 's' : ''}</p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">Actif</p>
              <p className="text-sm text-gray-600">Statut du profil</p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                <Calendar className="h-8 w-8 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {new Date().getFullYear()}
              </p>
              <p className="text-sm text-gray-600">Année en cours</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg mr-3">
              <Shield className="h-5 w-5 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Actions Rapides</h3>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {parent?.email_parent && (
              <a
                href={`mailto:${parent.email_parent}`}
                className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                <Mail className="h-5 w-5 text-green-600 mr-3" />
                <div>
                  <p className="font-medium text-green-900">Envoyer un email</p>
                  <p className="text-sm text-green-600">{parent.email_parent}</p>
                </div>
              </a>
            )}
            
            {parent?.contact_parent && (
              <a
                href={`tel:${parent.contact_parent}`}
                className="flex items-center p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Phone className="h-5 w-5 text-blue-600 mr-3" />
                <div>
                  <p className="font-medium text-blue-900">Appeler</p>
                  <p className="text-sm text-blue-600">{formatPhoneNumber(parent.contact_parent)}</p>
                </div>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentInfoTab; 